import { Component } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { JunoError } from '../juno-error/juno-error';
import { FormCheckClassAbstract } from '../../../abstract/form-check-class-abstract';
import { JunoCheckboxClasses } from '@dss-libs/shared/type/common-types';

/**
 * A styled, accessible single checkbox built on {@link FormCheckClassAbstract} and
 * integrated with Angular Signal Forms.
 *
 * The component wraps a native `<input type="checkbox">`. The native input is the control:
 * it is visually hidden (`sr-only`, so it stays focusable) and a sibling `<span>` draws the
 * box. That keeps the whole keyboard and screen-reader contract native — Space toggles,
 * focus lands where it should, the label is associated implicitly by the wrapping
 * `<label>` — while the visuals stay fully themable.
 *
 * Because it satisfies `FormCheckboxControl`, `[formField]` binds the field value to
 * `checked` rather than `value`. See {@link FormCheckClassAbstract} for why a checkbox
 * *group* is not this component.
 *
 * ---
 *
 * ### Inputs (own)
 *
 * None. Everything comes from the abstract base classes.
 *
 * ### Inputs (inherited)
 *
 * From {@link FormCheckClassAbstract}: `checked`.
 * From {@link FormControlStateAbstract}: `label`, `labelClass`, `ariaLabel`, `appliedClasses`
 * (a single class string applied to the box), `name`, `required`, `pattern`, `errors`,
 * `pending`, `touched`, `dirty`, `disabled`.
 *
 * ### Appearance
 *
 * Box size, radius, colours and the check animation mirror `<mat-checkbox>` as the app
 * configures it in `@global/styling/scss/components/mat-checkbox.css`, so this control and the
 * Material one can sit on the same screen without looking like two different checkboxes. The
 * tick draws itself in via `stroke-dashoffset` and is suppressed under
 * `prefers-reduced-motion`.
 *
 * ### Accessibility
 *
 * - The accessible name comes from the visible `label`, associated implicitly by the wrapping
 *   `<label>`. With no visible label, pass `ariaLabel` — a checkbox with neither has no name
 *   (WCAG 2.2 SC 4.1.2).
 * - `aria-invalid`, `aria-required`, `aria-busy` track the field state; `aria-describedby`
 *   points at the error block only while it is rendered, so it never dangles.
 * - The error block carries `role="alert"` so it is announced when it appears (SC 3.3.1).
 * - The required asterisk is `aria-hidden`; `aria-required` carries that to assistive tech.
 * - Space toggles and the tab order is native, because the real `<input>` is the control.
 *
 * ### Outputs (inherited)
 *
 * `touch` – emitted on blur so Signal Forms marks the field as touched.
 *
 * ---
 *
 * @usageNotes
 *
 * #### With Signal Forms (`[formField]`)
 * ```html
 * <juno-libs-ui-checkbox [formField]="registrationForm.acceptTerms" label="TERMS.ACCEPT" />
 * ```
 *
 * #### Standalone two-way binding (`[(checked)]`)
 * ```html
 * <juno-libs-ui-checkbox [(checked)]="rememberMe" label="LOGIN.REMEMBER_ME" name="remember-me" />
 * ```
 *
 * @see {@link FormCheckClassAbstract} for the contract and the group-vs-single distinction.
 * @see {@link JunoRadioGroup} for the single-choice-from-many counterpart.
 */
@Component({
  selector: 'juno-libs-ui-checkbox',
  imports: [JunoError, TranslocoDirective],
  templateUrl: './juno-checkbox.html',
  styleUrl: './juno-checkbox.css',
})
export class JunoCheckbox extends FormCheckClassAbstract<JunoCheckboxClasses> {}
